import { Button, Col, message, notification, Radio, Row, Table, Tag, Tooltip, Typography, Upload, } from "antd";
import { useGetColumnSearchPropsTypeString } from "../../../../hooks/FilterTable/objString";
import { DownloadOutlined, InboxOutlined } from "@ant-design/icons";
import { IUploadHistorico, IUploadTab } from "../../interfaces";
import { useColumnDateTimeSearch } from "../../../../hooks/FilterTable/date";
import { useRef, useState } from "react";
import { ColumnsType } from "antd/es/table";

import type { UploadProps, UploadFile, RadioChangeEvent } from "antd";

const UploadTab: React.FC<IUploadTab> = ({
    onUpload,
    onDownload,
    loading,
    accept = ".xls,.xlsx,.csv",
    multiple = false,
    data,
    loadingTable,
    themeState,
    loadingButtonId,
}) => {
    const refTotalFiltrado = useRef<IUploadHistorico[]>([]);

    const ColumnsHistoricoUploadTab: ColumnsType<IUploadHistorico> = [
        {
            title: "Arquivo",
            dataIndex: "nome",
            key: "nome",
            ellipsis: true,
            width: 250,
            ...useGetColumnSearchPropsTypeString("nome"),
        },
        {
            title: "Fonte",
            dataIndex: "fonte",
            key: "fonte",
            ellipsis: true,
            width: 150,
            ...useGetColumnSearchPropsTypeString("fonte"),
        },
        {
            title: "Importado por",
            dataIndex: "usuario_nome",
            key: "usuario_nome",
            ellipsis: true,
            width: 120,
            ...useGetColumnSearchPropsTypeString("usuario_nome"),
        },
        {
            title: "Importado em",
            dataIndex: "data_upload",
            key: "data_upload",
            width: 120,
            ...useColumnDateTimeSearch("data_upload"),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            filters: [
                { text: "Concluído", value: "Concluído" },
                { text: "Processando", value: "Processando" },
                { text: "Erro", value: "Erro" },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status: string) => {
                const color =
                    status === "Processando" ? "blue" :
                        status === "Concluído" ? 'green' :
                            status === 'Erro' ? 'red' :
                                "gray";
                return <Tag color={color}>{status?.toUpperCase()}</Tag>;
            },
        },
        {
            title: "Ação",
            width: 50,
            render: (_: any, record: IUploadHistorico) => {
                return (
                    <div className="divAcao">
                        <Tooltip title={'Baixar arquivo'} color={'blue'}>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={() => onDownload(record._id, record.url_download)}
                                loading={loadingButtonId === record._id}
                            />
                        </Tooltip>
                    </div>
                );
            },
        }
    ];

    const { Text } = Typography;

    const { Dragger } = Upload;

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [fontValue, setFontValue] = useState<number | null>(null);

    const isValidFile = (file: File) => {
        return (
            file.type === "application/vnd.ms-excel" ||
            file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.name.endsWith(".csv")
        );
    };

    const props: UploadProps = {
        name: "file",
        multiple,
        accept,
        fileList,

        beforeUpload(file) {
            if (!isValidFile(file)) {
                notification.error({
                    message: "Erro",
                    description:
                        "Apenas arquivos de planilha (.xls .xlsx .csv)",
                });
                return Upload.LIST_IGNORE;
            }

            setFileList([file]);

            return false;
        },

        onRemove() {
            setFileList([]);
        },

        onDrop(e) {
            const droppedFiles = Array.from(e.dataTransfer.files);

            const validFiles = droppedFiles.filter(isValidFile);

            if (validFiles.length === 0) {
                notification.error({
                    message: "Erro",
                    description:
                        "Apenas arquivos de planilha (.xls .xlsx .csv)",
                });
                return;
            }

            setFileList([validFiles[0] as any]);
        },
    };

    const handleSend = async () => {
        if (!fontValue) {
            message.warning("Selecione uma fonte primeiro");
            return;
        }

        if (!fileList.length) {
            message.warning("Selecione um arquivo primeiro");
            return;
        }

        await onUpload(fontValue, fileList[0] as any);
        setFileList([]);
        setFontValue(null);
    };

    const onChange = (e: RadioChangeEvent) => {
        setFontValue(e.target.value);
    };

    return (
        <>
            <Table
                columns={ColumnsHistoricoUploadTab}
                dataSource={data}
                rowKey={(record) => record._id}
                loading={loadingTable}
                size="small"
                pagination={{
                    locale: { items_per_page: "" },
                    showSizeChanger: true,
                    defaultPageSize: 10,
                    pageSizeOptions: ['10', '25', '50', '75', '100'],
                }}
                scroll={{ x: 1000 }}
                onChange={(_, __, ___, extra) => {
                    refTotalFiltrado.current = extra.currentDataSource;
                }}
                title={(registros) => {
                    return (
                        <div
                            style={{
                                backgroundColor:
                                    themeState === "light"
                                        ? "#FAFAFA"
                                        : "#1D1D1D",
                                padding: 10,
                                marginBottom: -8,
                                margin: -8,
                                textAlign: "end",
                            }}
                        >
                            <Text>
                                {`Mostrando: ${registros.length} de ${refTotalFiltrado.current.length > 0 ? refTotalFiltrado.current.length + ' em' : ''} 
                                um total de ${data.length} Receitas`}
                            </Text>
                        </div>
                    );
                }}
                footer={(registros) => {
                    return (
                        <div style={{ textAlign: "end" }}>
                            <Text>
                                {`Mostrando: ${registros.length} de ${refTotalFiltrado.current.length > 0 ? refTotalFiltrado.current.length + ' em' : ''} 
                                um total de ${data.length} Receitas`}
                            </Text>
                        </div>
                    );
                }}
            />
            <Row style={{ marginTop: 16 }}>
                <Col
                    lg={{ span: 12, offset: 6 }}
                    md={{ span: 24, offset: 0 }}
                    sm={{ span: 24, offset: 0 }}
                    xs={{ span: 24, offset: 0 }}
                >
                    <Col>
                        <Col
                            style={{
                                marginBottom: 8,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <Text
                                style={{
                                    fontWeight: 500,
                                }}
                            >
                                Selecione a fonte do arquivo:
                            </Text>
                            <Radio.Group onChange={onChange} value={fontValue}>
                                <Radio value={1}
                                    style={{
                                        fontWeight: 500,
                                    }}
                                >
                                    Marcos Inácio
                                </Radio>
                                <Radio value={2}
                                    style={{
                                        fontWeight: 500,
                                    }}
                                >
                                    Monteiro & Monteiro
                                </Radio>
                            </Radio.Group>
                        </Col>
                        <Dragger
                            {...props}
                            disabled={loading}
                            style={{ width: "100%" }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Clique ou arraste o arquivo para esta área
                            </p>
                            <p className="ant-upload-hint">
                                Apenas arquivos de planilha (.xls .xlsx .csv)
                            </p>
                        </Dragger>
                        <Button
                            type="primary"
                            onClick={handleSend}
                            loading={loading}
                            disabled={!fileList.length || !fontValue}
                            style={{
                                marginTop: 16,
                                width: "100%",
                            }}
                        >
                            Enviar arquivo
                        </Button>
                    </Col>
                </Col>
            </Row>
        </>
    );
};

export default UploadTab;
